import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { OnboardingCandidateClaimsCompleteService } from "../../pages/onboarding/onboarding-candidate-claims-complete.service";
import { WEBSOCKET_EVENT_TYPES } from "../constants/websocket-event-types.constants";
import { AppNotification } from "../components/notifications/app-notification.model";
import { getNotificationRegistryEntry } from "../components/notifications/notification-registry";
import { BillingSubscriptionLockoutService } from "../../core/billing/billing-subscription-lockout.service";
import { isBillingSubscriptionEndedWebSocketMessage } from "../../core/billing/billing-lockout.helper";
import type { BillingSubscriptionEndedReason } from "../../core/billing/billing-lockout.constants";
import { WebSocketMessage, WebSocketService } from "./websocket.service";

@Injectable({
  providedIn: "root",
})
export class WebSocketNotificationOrchestratorService {
  private readonly wsService = inject(WebSocketService);
  private readonly router = inject(Router);
  private readonly onboardingCompleteSvc = inject(
    OnboardingCandidateClaimsCompleteService,
  );
  private readonly billingLockoutSvc = inject(
    BillingSubscriptionLockoutService,
  );

  private readonly notificationsSubject = new BehaviorSubject<
    AppNotification[]
  >([]);
  private readonly notificationAddedSubject = new Subject<AppNotification>();

  private messagesSub?: Subscription;
  private notificationIdCounter = 0;

  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly notificationAdded$ = this.notificationAddedSubject.asObservable();

  get notifications(): AppNotification[] {
    return this.notificationsSubject.value;
  }

  getConnectionStatus(): Observable<boolean> {
    return this.wsService.getConnectionStatus();
  }

  async initialize(): Promise<void> {
    await this.wsService.connect();

    if (this.messagesSub) {
      return;
    }

    this.messagesSub = this.wsService
      .getMessages()
      .subscribe((message) => this.processMessage(message));
  }

  shutdown(): void {
    this.messagesSub?.unsubscribe();
    this.messagesSub = undefined;
    this.wsService.forceDisconnect();
  }

  async reconnect(): Promise<void> {
    await this.wsService.reconnect();
  }

  isDevWebSocketToggleAvailable(): boolean {
    return this.wsService.isDevWebSocketToggleAvailable();
  }

  isDevWebSocketConnectionEnabled(): boolean {
    return !this.wsService.isDevConnectionSuppressed();
  }

  async setDevWebSocketConnectionEnabled(enabled: boolean): Promise<void> {
    if (!this.wsService.isDevWebSocketToggleAvailable()) {
      return;
    }

    if (enabled) {
      await this.wsService.enableDevConnection();
    } else {
      this.wsService.disableDevConnection();
    }
  }

  removeNotification(notificationId: string): void {
    this.notificationsSubject.next(
      this.notifications.filter((item) => item.id !== notificationId),
    );
  }

  /** Marks only the given notifications as no longer new (seen in the bell menu). */
  markNotificationsAsSeen(notificationIds: string[]): void {
    if (notificationIds.length === 0) {
      return;
    }

    const seenIds = new Set(notificationIds);
    const updated = this.notifications.map((notification) =>
      seenIds.has(notification.id) && notification.isNew
        ? { ...notification, isNew: false }
        : notification,
    );
    this.notificationsSubject.next(updated);
  }

  private processMessage(message: WebSocketMessage): void {
    if (isBillingSubscriptionEndedWebSocketMessage(message)) {
      const reason = message.reason as
        | BillingSubscriptionEndedReason
        | undefined;
      this.billingLockoutSvc.promptReactivation({
        source: "websocket",
        websocketReason: reason,
      });
      return;
    }

    const entry = getNotificationRegistryEntry(message.type);
    if (!entry) {
      return;
    }

    if (entry.acceptMessage && !entry.acceptMessage(message)) {
      return;
    }

    if (message.type === WEBSOCKET_EVENT_TYPES.ONBOARDING_POTENTIAL_CASES_COMPLETED) {
      const storeId = message["storeId"];
      if (storeId) {
        this.onboardingCompleteSvc.promptNavigateToResults(String(storeId));
      }
    } else {
      entry.onReceived?.(message, this.createDispatchContext());
    }

    const notification: AppNotification = {
      id: `notification_${++this.notificationIdCounter}_${Date.now()}`,
      eventType: message.type,
      payload: message,
      receivedAt: new Date(),
      isNew: true,
    };

    const updated = [notification, ...this.notifications].slice(0, 50);
    this.notificationsSubject.next(updated);
    this.notificationAddedSubject.next(notification);
  }

  private createDispatchContext() {
    return {
      navigate: (routePath: string, queryParams?: Record<string, string>) => {
        void this.router.navigate([routePath], { queryParams });
      },
    };
  }
}
